import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import {
  DeliveryApply,
  HandoverApplyRequest,
  ShowPackageImportApplyRequest,
  canonicalShowPackageJson,
  type TDeliveryEnvelope,
  type THandoverBundle,
  type TShowPackageImportRequest,
  type TShowPackageImportPreview,
} from '@schemas'
import { transferApi, isUncertainTransferFailure, useTransferAction } from '@/api/show-transfer'

export type Choices = Pick<
  TShowPackageImportRequest,
  'mappings' | 'priorClasses' | 'resolutions' | 'clubAffiliations'
>
export const emptyChoices = (): Choices => ({
  mappings: [],
  priorClasses: [],
  resolutions: [],
  clubAffiliations: [],
})
export const referenceKey = (reference: unknown) => canonicalShowPackageJson(reference)
export const recoverySchema = z.strictObject({
  format: z.literal('honeyshow-review-recovery.v1'),
  installationKey: z.string(),
  handover: z.boolean(),
  request: z.unknown(),
})
export type ReviewInput = {
  packageValue: unknown
  installationKey: string
  delivery?: TDeliveryEnvelope
  bundle?: THandoverBundle
  baselines?: unknown[]
  disabled?: boolean
  onLocked?: (locked: boolean) => void
  onFinished?: () => void
}
export function canProposeCleanCreation(preview: TShowPackageImportPreview) {
  const context = preview.creationContext
  return (
    !context.show.exists &&
    !context.show.hasLineage &&
    !context.hasPortableBindings &&
    context.sharedPools.every(pool => pool.occupied === false)
  )
}
export function usePackageReview(input: ReviewInput) {
  const { packageValue, installationKey, bundle, delivery, disabled = false } = input
  const handover = Boolean(bundle)
  const [choices, setChoices] = useState(emptyChoices)
  const [baselines, setBaselines] = useState<unknown[]>(input.baselines ?? [])
  const [review, setReview] = useState<{ request: unknown; preview: TShowPackageImportPreview }>()
  const [frozen, setFrozen] = useState<unknown>()
  const [result, setResult] = useState<Awaited<ReturnType<typeof transferApi.apply>>>()
  const [error, setError] = useState('')
  const [uncertain, setUncertain] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [valid, setValid] = useState(false)
  const [reading, setReadingState] = useState(false)
  const readingFile = useRef(false)
  function setReading(value: boolean) {
    readingFile.current = value
    setReadingState(value)
    if (value) invalidate()
  }
  const generation = useRef(0),
    applying = useRef(false),
    mounted = useRef(true)
  const latest = useRef({ disabled, uncertain, result, valid })
  latest.current = { disabled, uncertain, result, valid }
  const applyAction = useTransferAction((body: unknown) =>
    delivery ? transferApi.apply(body, handover, true) : transferApi.apply(body, handover)
  )
  const request = (next: Choices, chain: unknown[]) =>
    delivery
      ? { envelope: delivery, choices: next }
      : bundle
        ? { bundle, choices: next }
        : { package: packageValue, ...(chain.length ? { baselines: chain } : {}), ...next }
  const invalidate = () => {
    generation.current++
    setValid(false)
    setUpdating(false)
  }
  function reset() {
    if (applying.current || uncertain) return
    invalidate()
    setChoices(emptyChoices())
    setBaselines([])
    setReview(undefined)
    setFrozen(undefined)
    setResult(undefined)
    setError('')
  }
  // External artifact/baseline changes must invalidate an in-flight response as well as its token.
  const artifactIdentity = useMemo(
    () => canonicalShowPackageJson(delivery ?? bundle ?? packageValue ?? null),
    [delivery, bundle, packageValue]
  )
  const baselineIdentity = useMemo(
    () => canonicalShowPackageJson(input.baselines ?? []),
    [input.baselines]
  )
  // The artifact a generation belongs to. A preview can start from a render whose
  // artifact reset effect has not run yet (a click right after a file loads); that
  // preview already uses the new artifact, so the late reset must not discard it.
  const artifactKey = `${installationKey}\u0000${artifactIdentity}`
  const generationArtifact = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (generationArtifact.current === artifactKey) return
    generationArtifact.current = artifactKey
    invalidate()
    setReview(undefined)
  }, [artifactKey])
  useEffect(() => {
    invalidate()
    setBaselines(input.baselines ?? [])
  }, [baselineIdentity])
  useEffect(() => {
    if (disabled && !uncertain) invalidate()
  }, [disabled, uncertain])
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      generation.current++
    }
  }, [])
  async function preview(next = choices, chain = baselines, proposeCreation = false) {
    if (disabled || applying.current || uncertain || result) return
    const current = ++generation.current
    if (generationArtifact.current !== artifactKey) {
      // Starting for a new artifact before its reset effect: apply that reset now.
      generationArtifact.current = artifactKey
      setReview(undefined)
    }
    setValid(false)
    setUpdating(true)
    setError('')
    const isCurrent = () => mounted.current && current === generation.current
    const send = (body: unknown) =>
      delivery ? transferApi.preview(body, handover, true) : transferApi.preview(body, handover)
    let canonical = structuredClone(request(next, chain))
    try {
      let response = await send(canonical)
      if (!isCurrent()) return
      if (proposeCreation && !bundle && !delivery && canProposeCleanCreation(response)) {
        const additions = response.creationContext.proposedCreates.filter(
          reference =>
            !next.mappings.some(
              mapping => referenceKey(mapping.reference) === referenceKey(reference)
            )
        )
        if (additions.length) {
          next = {
            ...next,
            mappings: [
              ...next.mappings,
              ...additions.map(reference => ({ reference, action: 'create' as const })),
            ],
          }
          setChoices(next)
          canonical = structuredClone(request(next, chain))
          response = await send(canonical)
          if (!isCurrent()) return
        }
      }
      setReview({ request: canonical, preview: response })
      setValid(true)
    } catch (e) {
      if (isCurrent())
        setError(
          `Unable to validate import: ${e instanceof Error ? e.message : 'server validation failed'}. Check import again.`
        )
    } finally {
      if (isCurrent()) setUpdating(false)
    }
  }
  function changeChoices(next: Choices, refresh = false) {
    if (applying.current || uncertain || result) return
    invalidate()
    setChoices(next)
    setFrozen(undefined)
    setError('')
    if (refresh) void preview(next)
  }
  function changeBaselines(chain: unknown[]) {
    if (applying.current || uncertain || result) return
    invalidate()
    setBaselines(chain)
    if (review) void preview(choices, chain)
  }
  const canApply =
    valid &&
    !reading &&
    !updating &&
    !disabled &&
    !uncertain &&
    !result &&
    Boolean(
      review?.preview.canApply &&
      !review.preview.blockers.length &&
      Date.parse(review.preview.expiresAt) > Date.now()
    )
  async function apply() {
    if (applying.current || readingFile.current || disabled || result || (!uncertain && !canApply))
      return
    const body =
      frozen ??
      (review
        ? { ...(review.request as object), reviewToken: review.preview.reviewToken }
        : undefined)
    if (!body) return
    applying.current = true
    generation.current++
    setUpdating(false)
    setFrozen(body)
    setError('')
    try {
      const completed = await applyAction.mutateAsync(body)
      if (!mounted.current) return
      setResult(completed)
      setUncertain(false)
      setValid(false)
      input.onFinished?.()
    } catch (e) {
      if (!mounted.current) return
      const ambiguous = isUncertainTransferFailure(e)
      setUncertain(ambiguous)
      setValid(false)
      if (!ambiguous) {
        setFrozen(undefined)
        setReview(undefined)
      }
      setError(
        ambiguous
          ? 'We lost the connection while importing. Check whether it completed.'
          : `${e instanceof Error ? e.message : 'Import failed'}. Preview again before applying. For a stale handover checkpoint, cancel the grant and prepare a new transfer.`
      )
    } finally {
      applying.current = false
    }
  }
  function recover(value: unknown) {
    if (applying.current || disabled || uncertain || result) return
    try {
      const saved = recoverySchema.parse(value)
      if (saved.installationKey !== installationKey || saved.handover !== handover)
        throw new Error()
      ;(delivery
        ? DeliveryApply
        : handover
          ? HandoverApplyRequest
          : ShowPackageImportApplyRequest
      ).parse(saved.request)
      const body = saved.request as Record<string, unknown>
      if (
        referenceKey(delivery ? body.envelope : handover ? body.bundle : body.package) !==
        artifactIdentity
      )
        throw new Error()
      invalidate()
      setFrozen(saved.request)
      setUncertain(true)
      setReview(undefined)
      setError('Recovery request loaded. Check import result to retrieve its receipt.')
    } catch {
      setError('This recovery file is invalid or belongs to another installation or workflow.')
    }
  }
  const refresh = useRef(() => {})
  refresh.current = () => {
    if (
      review &&
      latest.current.valid &&
      !latest.current.disabled &&
      !latest.current.uncertain &&
      !latest.current.result &&
      !applying.current
    )
      void preview()
  }
  useEffect(() => {
    const focus = () => refresh.current()
    window.addEventListener('focus', focus)
    return () => window.removeEventListener('focus', focus)
  }, [])
  useEffect(() => {
    if (!valid || !review || uncertain || result) return
    const delay = Date.parse(review.preview.expiresAt) - Date.now()
    if (delay <= 0) {
      setValid(false)
      return
    }
    const timer = setTimeout(
      () => {
        setValid(false)
        refresh.current()
      },
      Math.min(delay + 1, 2147483647)
    )
    return () => clearTimeout(timer)
  }, [review, valid, uncertain, result])
  const locked = reading || updating || applyAction.isPending || uncertain
  useEffect(() => {
    input.onLocked?.(locked)
  }, [locked, input.onLocked])
  useEffect(() => {
    const leave = (event: BeforeUnloadEvent) => {
      if (locked) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', leave)
    return () => window.removeEventListener('beforeunload', leave)
  }, [locked])
  return {
    ...input,
    handover,
    choices,
    baselines,
    review,
    frozen,
    result,
    error,
    uncertain,
    updating,
    valid,
    canApply,
    reading,
    setReading,
    locked,
    applying: applyAction.isPending,
    preview,
    apply,
    recover,
    changeChoices,
    changeBaselines,
    reset,
    refresh: () => refresh.current(),
  }
}
export type ReviewController = ReturnType<typeof usePackageReview>
